import { watch, type MaybeRefOrGetter } from 'vue'
import {
  useQuery,
  type DefaultError,
  type QueryClient,
  type QueryKey,
  type UseQueryOptions,
  type UseQueryReturnType
} from '@tanstack/vue-query'
import { useMessage } from '@/composables/useMessage'
import { catchErrorHandler } from '@/utils/api/error-handler'
import {
  isDomainApiError,
  isSkippableQueryError,
  type DomainApiError
} from '@/queries/shared/domain-api-error'
import {
  resolveDomainErrorMessage,
  type AppMessage,
  type DomainErrorHandlingResult
} from '@/queries/shared/domain-error-message'

type QueryErrorMessageResolver = (context: {
  error: DomainApiError
}) => AppMessage | false | undefined

export type AppQueryOptions = {
  /** 字串/descriptor/resolver/false，預設用 resultCode 對應的 i18n key，沒翻譯則 fallback Common.Response.Error */
  errorMessage?: AppMessage | false | QueryErrorMessageResolver
  /** 攔截特定錯誤碼做自訂處理（例如切換成表單內聯提示），回傳 { handled: true } 時不會再顯示預設訊息 */
  onDomainError?: (
    error: DomainApiError
  ) => DomainErrorHandlingResult | Promise<DomainErrorHandlingResult>
  /**
   * 非 DomainApiError（網路錯誤等非預期例外）的處理方式。
   * 預設呼叫 catchErrorHandler 記錄到 console，方便開發排查，但不會跳訊息打擾使用者。
   * 傳 false 可完全靜默，供刻意不處理錯誤的輔助性查詢使用（例如跑馬燈、篩選下拉選單）。
   */
  onUnhandledError?: ((error: unknown) => void) | false
}

const defaultOnUnhandledError = (error: unknown) => {
  try {
    catchErrorHandler(error)
  } catch (loggedError) {
    // catchErrorHandler 設計上是給「呼叫端 try/catch 再顯示」的場景用，這裡沒有更上層的呼叫端能接，
    // 改成單純記錄，避免在 watch callback 裡拋出未捕捉例外
    console.error(loggedError)
  }
}

/** 對稱 useAppMutation：包一層 useQuery，watch 查詢的 error 並自動處理 domain error 訊息 */
export const useAppQuery = <
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(
  queryOptions: MaybeRefOrGetter<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryFnData, TQueryKey>
  >,
  appOptions: AppQueryOptions = {},
  queryClient?: QueryClient
): UseQueryReturnType<TData, TError> => {
  const { openMessage } = useMessage()

  const query = useQuery(queryOptions, queryClient)

  watch(query.error, async (error) => {
    if (!error || isSkippableQueryError(error)) return

    if (!isDomainApiError(error)) {
      if (appOptions.onUnhandledError !== false) {
        ;(appOptions.onUnhandledError ?? defaultOnUnhandledError)(error)
      }
      return
    }

    const handlingResult = await appOptions.onDomainError?.(error)
    if (handlingResult?.handled) return

    const message = resolveDomainErrorMessage(
      error,
      { error },
      { errorMessage: appOptions.errorMessage }
    )
    if (message !== null) {
      openMessage('error', message)
    }
  })

  return query
}
